import type { Dispatch } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import type { MouseEvent } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { NavigateFunction } from 'react-router-dom';
import { FlowTypes, SELECTED_METHOD_ID } from 'store/constants';
import type { UIPayment, UIPaymentGroups } from 'store/formatters';
import type {
  BonusTier,
  CurrencyDisplaySetting,
  Limits,
  LostProfitInfo,
  MerchantUserFeeResponce,
  Option,
  Parameter,
  PayGroupCategories,
  PayHubDomainSessionsEnumsPlatformType,
  PayHubWidgetContractsUserCardResponse,
  ProcessingCurrency,
  UserBonusInfo,
} from 'store/models';
import { getBonusMinDepositAmount } from 'store/models';
import {
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType as ApiParameter,
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType as ExtendedTypes,
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType,
  PayHubEnumsCashierMethodParamType,
} from 'store/models';
import { setCurrentMethod } from 'store/slices/deposit';
import { setSelectedPayGroupId, unlockFlow } from 'store/slices/global';
import { setOTPCodeFieldName } from 'store/slices/otp';
import type { SelectedCardType } from 'store/slices/userCard';
import { cleanCardState } from 'store/slices/userCard';
import type { SelectActionDepositMethodProps } from 'utils';
import { getFormDefaultValues, isEmpty, setNullToNotRequiredFields, softLocalStorageSet } from 'utils';
import { getFormattedAmountWithCurrency, getPrettierFormatNumber } from 'utils/dataTransform';
import { getMerchantUserFeeTranslation } from 'utils/fee';
import { isMethodAvailable } from 'utils/getIsMethodActive';

import type { DynamicTypeObject, FlatStringObject, TFunction } from '../../commonTypes';
import { multiply, normalizedStringValue, percentage, truncateNumber } from '../../utils/math/math';
import { calculateUserFee } from '../../utils/userFee';
import { redirectToSelectedMethod } from '../routerUtils';
import { Routes } from '../routes';
import { PaymentProcessStrategy } from '../SubmitStrategy/PaymentProcessStrategy';

const METHODS_WITH_FORM: string[] = [
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.Activity,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.Info,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.Onramp,
];

export const selectActionMethod = (props: SelectActionDepositMethodProps): void => {
  const { method, navigate, dispatch, flowType } = props;
  dispatch(setCurrentMethod(method));
  dispatch(setSelectedPayGroupId(method.id));
  softLocalStorageSet(SELECTED_METHOD_ID, JSON.stringify(method.id));
  redirectToSelectedMethod(method.id, navigate, flowType);
};

interface SelectDepositMethodHandlerProps {
  method: UIPaymentGroups;
  platform?: PayHubDomainSessionsEnumsPlatformType;
  dispatch: Dispatch;
  navigate: NavigateFunction;
  flowType: FlowTypes;
  descriptionUrl?: string;
  currentPayGroupId?: number;
}
export const selectDepositMethodHandler = (
  props: SelectDepositMethodHandlerProps,
  event?: MouseEvent,
): boolean | undefined => {
  const { method, dispatch, navigate, flowType, currentPayGroupId } = props;
  if (!isMethodAvailable(method)) {
    event?.stopPropagation();
    event?.preventDefault();
    dispatch(unlockFlow());
    return false;
  }

  if (method.id !== currentPayGroupId) {
    dispatch(cleanCardState());
  }

  if (METHODS_WITH_FORM.includes(method.payGroupActionType)) {
    dispatch(setSelectedPayGroupId(method.id));
    softLocalStorageSet(SELECTED_METHOD_ID, JSON.stringify(method.id));
    selectActionMethod({
      method,
      dispatch,
      navigate,
      flowType,
    });
  }

  dispatch(unlockFlow());
  return undefined;
};

interface FormMethodSubTitleProps {
  t: TFunction;
  processingTime?: string;
  isIncludeBonus?: boolean;
  fee: MerchantUserFeeResponce | string;
  lostProfitInfo?: LostProfitInfo | null;
  limits: Limits;
  currency?: string;
}

export const formMethodSubTitle = (props: FormMethodSubTitleProps): string => {
  const { t, processingTime = '', fee, lostProfitInfo, isIncludeBonus = false, limits, currency = '' } = props;

  const separator = ' • ';
  const bonusKey = isIncludeBonus ? 'PH.BONUS_INCLUDED' : '';
  const bonusText = t(bonusKey);
  const processingTimeText = t(processingTime) || '';
  const feeText = getMerchantUserFeeTranslation(fee, t);
  const minLimit = limits?.min || 0;
  const limitText =
    limits.displayLimits && minLimit > 0
      ? t('PH.MIN_LIMIT_INFO_TEXT', {
          amount: normalizedStringValue(`${minLimit}`),
          currency,
        })
      : '';

  const subTitleArr = lostProfitInfo
    ? [
        limitText,
        t(lostProfitInfo.text, lostProfitInfo.props as FlatStringObject),
        processingTimeText,
        feeText,
        bonusText,
        bonusText,
      ]
    : [limitText, processingTimeText, feeText, bonusText];

  return subTitleArr.filter((element) => Boolean(element)).join(separator);
};

export const buildCardsData = (card: SelectedCardType | null | undefined) => {
  const cardId = card?.id ? String(card.id) : '';
  return {
    card_id: card?.isNew ? undefined : cardId,
    card_bin: card?.bin,
  };
};

const prepareExpirationDate = (expMonth: number | null | undefined, expYear: number | null | undefined): string => {
  if (expMonth && expYear) {
    const croppedExpYear = expYear % 100;
    const formattedExpMonth = `${expMonth}`.padStart(2, '0');
    return `${formattedExpMonth}/${croppedExpYear}`;
  }
  return '';
};

export const normalizeParamWithSpaces = (field: string) => {
  const trimmedName = field.trim();
  return trimmedName.replace(/\s+/g, ' ');
};

interface BuildDefaultCheckoutValuesProps {
  card?: PayHubWidgetContractsUserCardResponse | null;
  amount?: string;
  formConfig?: Parameter[];
}

export const buildDefaultCheckoutValues = (props: BuildDefaultCheckoutValuesProps) => {
  const { card = null, amount = '', formConfig } = props;

  const selectFields = formConfig?.filter(
    ({ type, extendedType }) =>
      type === 'select' && ![`${ExtendedTypes.CardPan}`, `${ExtendedTypes.CreditCard}`].includes(`${extendedType}`),
  );

  const selectDefaultValues = getFormDefaultValues(selectFields || []);

  const defaultValues = {
    expirationDate: prepareExpirationDate(card?.exp_month, card?.exp_year),
    amount,
    [ApiParameter.CardCardholderName]: normalizeParamWithSpaces(card?.cardholder_name || ''),
    [ApiParameter.CardCvv]: '',
    [ApiParameter.CardId]: card?.id,
    save_card: true,
    card_pan: '',
  };

  return Object.keys(selectDefaultValues)
    ? {
        ...defaultValues,
        ...selectDefaultValues,
      }
    : defaultValues;
};

export const prepareFieldsDataForSubmit = (
  fields: FieldValues,
  selectedCard: SelectedCardType | null,
  requiredFields: Parameter[],
  dirtyFields?: Partial<Record<keyof FieldValues, boolean>>,
) => {
  const preparedFields: DynamicTypeObject = setNullToNotRequiredFields(requiredFields, fields, dirtyFields);

  const result: Record<string, string | number | unknown> = {};
  Object.keys(preparedFields).forEach((item) => {
    const fieldValue: string | number | unknown | Option = preparedFields[item];
    const fieldType = requiredFields.find((current) => current.name === item)?.type;

    if (fieldType === 'date') {
      result[item] = dayjs(fieldValue as Date).toISOString();
    } else if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
      result[item] = fieldValue.value;
    } else {
      result[item] = fieldValue;
    }
  });

  if (!isEmpty(selectedCard)) {
    return {
      ...result,
      ...buildCardsData(selectedCard),
    };
  }

  return result;
};

export const excludeOTPField = (isOTPEnabled: boolean, fields: Parameter[]) =>
  !isOTPEnabled ? fields : fields.filter(({ isOtpRequisites }) => !isOtpRequisites);

export const checkAndPrepareForPAM = (fields: Parameter[], isPamNewAccount: boolean) =>
  fields.map((field: Parameter) => {
    if (field.verifyNewPayoutAccount && isPamNewAccount) {
      return {
        ...field,
        type: 'input',
        value: undefined,
      };
    }
    return field;
  });

export const getSubmitStrategy = () => new PaymentProcessStrategy();

export type Amount = string | number;

interface GetBonusTextProps {
  t: TFunction;
  currency: string;
  amount: Amount;
  bonusInfo: UserBonusInfo;
  currencyPrecision?: number;
  currencyDisplaySetting?: CurrencyDisplaySetting;
}

interface GetCalculatedBonusProps {
  absoluteBonusValue?: number | null;
  percentBonusValue?: number | null;
  amountAsNumber: number;
  maxDepositAmount: number;
  maxBonusValue?: number | null;
}

export const getCalculatedBonus = (props: GetCalculatedBonusProps): number => {
  const {
    absoluteBonusValue = 0,
    percentBonusValue = 0,
    amountAsNumber,
    // maxDepositAmount = 0,
    maxBonusValue = 0,
  } = props;

  if (absoluteBonusValue) {
    return absoluteBonusValue;
  }

  let calculatedBonusAmount = 0;

  if (percentBonusValue) {
    const calculatedBonusAmountBig = percentage(amountAsNumber, percentBonusValue);
    calculatedBonusAmount = calculatedBonusAmountBig.toNumber();
    // TODO: should be deleted if we don't need it
    // if (maxDepositAmount && amountAsNumber > maxDepositAmount) {
    //   return ((maxDepositAmount * percentBonusValue) / 100);
    // }
  }

  if (maxBonusValue && calculatedBonusAmount > maxBonusValue) {
    return maxBonusValue;
  }

  return calculatedBonusAmount;
};

const getActiveTier = (tiers: BonusTier[] | undefined, amount: number): BonusTier | undefined => {
  if (!tiers?.length) return undefined;
  return [...tiers]
    .sort((a, b) => (b.min_deposit_amount ?? 0) - (a.min_deposit_amount ?? 0))
    .find((tier) => {
      const tierMin = tier.min_deposit_amount ?? 0;
      const tierMax = tier.max_deposit_amount ?? Infinity;
      return amount >= tierMin && amount <= tierMax;
    });
};

export const getBonusButtonText = (props: GetBonusTextProps): string => {
  const { t, amount, currency, bonusInfo, currencyDisplaySetting, currencyPrecision = 2 } = props;
  const { maxBonusValue = 0, maxDepositAmount = 0, tiers } = bonusInfo;
  const minDepositAmount = getBonusMinDepositAmount(bonusInfo);
  const amountAsNumber: number = Number(amount) || 0;

  const activeTier = getActiveTier(tiers, amountAsNumber);
  const absoluteBonusValue = activeTier?.absolute_bonus_value ?? bonusInfo.absoluteBonusValue;
  const percentBonusValue = activeTier?.percent_bonus_value ?? bonusInfo.percentBonusValue ?? 0;
  const tierMaxBonusValue = activeTier?.max_bonus_value ?? maxBonusValue;
  const tierMaxDepositAmount = activeTier?.max_deposit_amount ?? maxDepositAmount;

  let calculatedAmount = 0;

  if (amountAsNumber && minDepositAmount && amountAsNumber >= minDepositAmount) {
    calculatedAmount = getCalculatedBonus({
      absoluteBonusValue,
      percentBonusValue,
      amountAsNumber,
      maxDepositAmount: tierMaxDepositAmount,
      maxBonusValue: tierMaxBonusValue,
    });
  }

  if (calculatedAmount) {
    const formattedAmount = truncateNumber(calculatedAmount, currencyPrecision).toString();

    return t('PH.BUTTON.BONUS', {
      amount: getFormattedAmountWithCurrency(
        getPrettierFormatNumber(formattedAmount),
        currency,
        currencyDisplaySetting,
      ),
    });
  }

  return '';
};

type ConversionProps = {
  amount: Amount;
  processingCurrency?: ProcessingCurrency;
  processingCurrencyRate?: number;
  currencyPrecision?: number;
  currencyDisplaySetting?: CurrencyDisplaySetting;
};

export const getConversionText = ({
  amount,
  processingCurrency,
  processingCurrencyRate,
  currencyDisplaySetting,
  currencyPrecision,
}: ConversionProps) => {
  if (!processingCurrency || !processingCurrencyRate) {
    return `${getPrettierFormatNumber(amount)}`;
  }

  const amountAsNumber = typeof amount === 'number' ? amount : truncateNumber(amount, currencyPrecision);
  const alpha3 = processingCurrency?.alpha3 || '';
  const convertedAmountBig = multiply(amountAsNumber, processingCurrencyRate);
  const convertedAmount = truncateNumber(convertedAmountBig, currencyPrecision);
  let normalizedAmount: string | number = convertedAmount;
  if (currencyPrecision === 2) {
    normalizedAmount = convertedAmount.toFixed(2);
  }

  return getFormattedAmountWithCurrency(getPrettierFormatNumber(normalizedAmount), alpha3, currencyDisplaySetting);
};

interface GetSubmitSubTextProps {
  t: TFunction;
  currency: string;
  amount: Amount;
  bonusInfo?: UserBonusInfo;
  isBonusIncluded?: boolean;
  processingCurrency?: ProcessingCurrency;
  processingCurrencyRate?: number;
  currencyDisplaySetting?: CurrencyDisplaySetting;
  merchantUserFee?: MerchantUserFeeResponce;
  currencyPrecision?: number;
}

interface GetFormattedFeeSubTextProps {
  amount: Amount;
  merchantUserFee?: MerchantUserFeeResponce;
  currencyPrecision?: number;
}

const getFeeAmountSubtext = ({ amount, merchantUserFee, currencyPrecision }: GetFormattedFeeSubTextProps) => {
  const amountAsNumber = Number(amount);
  const { userFeeAmount } = calculateUserFee(amountAsNumber, merchantUserFee, currencyPrecision);

  return truncateNumber(userFeeAmount, currencyPrecision);
};

export const getSubmitSubText = (props: GetSubmitSubTextProps): string => {
  const {
    t,
    currency,
    amount,
    bonusInfo,
    isBonusIncluded,
    processingCurrency,
    processingCurrencyRate,
    currencyDisplaySetting,
    merchantUserFee,
    currencyPrecision,
  } = props;
  const messages: string[] = [];

  if (processingCurrency && processingCurrencyRate) {
    messages.push(
      getConversionText({
        amount,
        processingCurrency,
        processingCurrencyRate,
        currencyDisplaySetting,
        currencyPrecision,
      }),
    );
  }

  const feeAmountSubText = getFeeAmountSubtext({
    amount,
    merchantUserFee,
    currencyPrecision,
  });

  if (merchantUserFee && feeAmountSubText) {
    const tKey = 'PH.DEPOSIT.LABEL.FEE';
    messages.push(`${t(tKey)} ${getFormattedAmountWithCurrency(feeAmountSubText, currency, currencyDisplaySetting)}`);
  }

  if (bonusInfo && isBonusIncluded) {
    messages.push(
      getBonusButtonText({
        t,
        amount,
        currency,
        bonusInfo,
        currencyDisplaySetting,
        currencyPrecision,
      }),
    );
  }

  return messages.length ? messages.join('; ') : '';
};

interface ValidPresetAmount {
  presetAmount: number;
  min?: number;
  max?: number;
}

interface ShouldSetAmountFromBlocksProps {
  isCategoryScrollLocked: boolean;
  amountTouched: boolean;
  amountBlocks?: number[];
  isSuccess: boolean;
}

export const shouldSetAmountFromBlocks = ({
  isCategoryScrollLocked,
  amountTouched,
  amountBlocks,
  isSuccess,
}: ShouldSetAmountFromBlocksProps): boolean =>
  isCategoryScrollLocked && !amountTouched && !!amountBlocks?.length && isSuccess;

const isNumberInRange = (value: number, min?: number, max?: number) =>
  value && (!min || value >= min) && (!max || value <= max);

export const getValidPresetAmount = ({ presetAmount, min, max }: ValidPresetAmount) => {
  if (isNumberInRange(presetAmount, min, max)) return presetAmount;
  if (min && presetAmount < min) return min;
  if (max && presetAmount > max) return max;
  return 1;
};

interface SetDepositInitDataToStoreProps {
  initData: UIPayment;
  dispatch: Dispatch;
  navigate: NavigateFunction;
  payGroupId?: number;
}

export const setDepositInitDataToStore = (props: SetDepositInitDataToStoreProps) => {
  const { initData, dispatch, navigate, payGroupId } = props;

  if (!payGroupId) {
    return;
  }

  const method = initData.methods.find(({ id }) => id === payGroupId);

  if (!method) {
    return;
  }

  selectDepositMethodHandler(
    {
      method,
      platform: initData?.widgetSettings?.platform,
      dispatch,
      navigate,
      flowType: FlowTypes.deposit,
    },
    {} as MouseEvent,
  );
};

export const sortFields = (fields: Parameter[]) => {
  const fieldsCopy = [...fields];
  fieldsCopy?.sort((a: Parameter, b: Parameter) => {
    if (a.type === PayHubEnumsCashierMethodParamType.FileUpload) return 1;
    if (b.type === PayHubEnumsCashierMethodParamType.FileUpload) return -1;
    if (a.extendedType === PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType.DisabledInputCopy)
      return 1;
    if (b.extendedType === PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType.DisabledInputCopy)
      return -1;
    return 0;
  });
  return fieldsCopy;
};

interface UploadErrorMessageProps {
  t: TFunction;
  errorMessage?: string;
}

export const getUploadErrorMessage = ({ errorMessage, t }: UploadErrorMessageProps) => {
  const key = errorMessage ? 'PH.DEPOSIT.UPLOAD_DOC.LIMIT_UPLOAD_ERROR' : 'PH.DEPOSIT.UPLOAD_DOC.UPLOAD_ERROR';

  return t(`${key}`);
};

export const filterAndSortConfigParamsForOTP = (() => {
  /**
   * Old logic:
   * temporary last field with isOtpRequisites:false & isRequired - is codeField
   * get name for generateCode and delete field, because used OTP component.
   * sort config - last fields isOtpRequisites
   * */
  let codeFieldName: string | null = null;

  return (data: Parameter[], dispatch: Dispatch) => {
    if (data?.length) {
      const currentCodeFieldName = data[data.length - 1]?.name || '';

      if (codeFieldName !== currentCodeFieldName) {
        codeFieldName = currentCodeFieldName;
        dispatch(setOTPCodeFieldName(currentCodeFieldName));
      }
    }

    return data.slice(0, -1).sort((a, b) => {
      if (a.isOtpRequisites === b.isOtpRequisites) return 0;
      return a.isOtpRequisites ? 1 : -1;
    });
  };
})();

export const navigateToFirstCategoryMethod = (
  categoryName: string,
  paygroupCategories: PayGroupCategories | null | undefined,
  methods: UIPaymentGroups[],
  navigate: NavigateFunction,
  flowType: FlowTypes,
  dispatch?: Dispatch,
  getUserSelectedGroup?: (categoryName: string) => number | null,
): void => {
  const categoryConfig = paygroupCategories?.categories?.find((cat) => cat.category_name === categoryName);

  if (!categoryConfig) return;

  const categoryMethods = methods.filter(
    (method) => categoryConfig.pay_group_ids.includes(method.id) && isMethodAvailable(method),
  );

  if (categoryMethods.length > 0) {
    const savedMethodId = getUserSelectedGroup ? getUserSelectedGroup(categoryName) : null;
    const savedMethod = savedMethodId ? categoryMethods.find((m) => m.id === savedMethodId) : null;
    const methodToNavigate = savedMethod ?? categoryMethods[0];
    dispatch?.(setCurrentMethod(methodToNavigate));
    navigate(`/${flowType}/${methodToNavigate.id}`, {
      state: {
        from: flowType === 'deposit' ? Routes.deposit : Routes.withdrawal,
      },
      replace: true,
    });
  }
};
